
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { isSearchSpace, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { ChannelTypes } from '../../../home/types/ChannelTypes';
import { SupportedContentTypes } from '../../../home/types/HomeTypes';
import { PopularExplanation, SimiliarUsersExplanation } from '../../../recommendations/models/IViewRecommendationInfo';
import { searchTelemetry } from '../../../search/components/SearchTelemetry';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { SiteRoles } from '../../../userManagement/users/serverApi/types/ISiteRole';
import { Telemetry } from '../../services/Telemetry';
import { ContentTelemetryEvents, TelemetryEvents } from '../TelemetryEvents';
import {
  ActionMenuItemClickedEvent,
  ContentClickedEvent,
  ContentLocation,
  ContentTelemetryCommonData,
  ContentTelemetryContextSpecificData,
  FavoriteToggleClickedEvent,
  FavoriteToggleType,
} from '../types/ContentTelemetryTypes';

export function checkIsSample(workbookName: String): boolean {
  const sampleNames: Array<String> = ['Superstore', 'World Indicators', 'Weltindikatoren', 'Supertienda', 'Indicadores Mundiales', 'Hypermarché',
                                  'Indicateurs Mondiaux', 'Supermercato', 'Indicatori mondiali', 'スーパーストア', '世界指標', '슈퍼스토어',
                                  '세계 지표', 'Superloja', 'Indicadores Mundiais', '示例超市', '世界指标', '超級市場', '世界指標'];
  return sampleNames.includes(workbookName);
}

export function generateTelemetryEventHandler(
  type: ContentTelemetryEvents,
  spaceInfo: SpaceInfo,
  allContents: ReadonlyArray<SupportedContentTypes>,
  contentInfo: IContentInfo,
  contentLocation: ContentLocation,
  contentPosition: number,
  specificData: ContentTelemetryContextSpecificData): (...args: any[]) => void {
  let collectionContentLocation;
  if (contentLocation !== ContentLocation.SpaceHeader) {
    if(spaceInfo.spaceType === 'collections'){
      collectionContentLocation = ContentLocation.Collections;
    } else if(spaceInfo.spaceType === 'collection') {
      collectionContentLocation = ContentLocation.Collection;
    }
  }

  let isSample: boolean = false;
  let workbookName: String = contentInfo.name;

  if(contentInfo.contentType === 'workbook') {
    isSample = checkIsSample(workbookName);
  }
  if(contentInfo.contentType === 'flow') {
    isSample = workbookName === 'Superstore Flow';
  }
  if(contentInfo.contentType === 'datasource') {
    isSample = workbookName === 'Superstore Datasource';
  }

  const commonData: ContentTelemetryCommonData = {
    contentId: contentInfo.id,
    contentType: contentInfo.contentType,
    contentLocation: collectionContentLocation || contentLocation,
    space: spaceInfo.spaceType,
    subspace: spaceInfo.subspaceName,
    contentPosition: contentPosition,
    isSample: isSample,
  };

  // if the content is a recommendation add meta data to telemetry event
  if (commonData.contentType && commonData.contentType === 'recommendedView' && contentInfo.explanationType) {
    const recommendationType: ChannelTypes = mapExplanationTypeToRecType(contentInfo.explanationType);

    specificData = {
      ...specificData,
      recommendationsMetadata: JSON.stringify({
        modelId: contentInfo.modelId,
        seenViews: allContents.slice(0, contentPosition).map(x => parseInt(x.id, 10)),
        recommendationType,
      })
    };
  }

  switch (type) {
    case TelemetryEvents.ContentClicked: {
      return function onContentClick() {
        if (isSearchSpace(spaceInfo)) {
          searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: 'search-space',
            resultType: spaceInfo.subspaceName,
            seeAll: false,
            index: contentPosition,
            total: (specificData as any).contentTotal,
            searchTerm: (specificData as any).searchTerm,
            contentId: contentInfo.id,
          });
        } else {
          Telemetry.sendEvent<ContentClickedEvent>(
            { ...commonData, ...specificData, type: TelemetryEvents.ContentClicked }
          );
        }
      };
    }
    case TelemetryEvents.FavoriteToggleClicked: {
      return function onFavoriteToggleClick(toggleType: FavoriteToggleType) {
        Telemetry.sendEvent<FavoriteToggleClickedEvent>(
          { ...commonData, ...specificData, type: TelemetryEvents.FavoriteToggleClicked, toggleType }
        );
      };
    }
    case TelemetryEvents.ActionMenuItemClicked: {
      return function onActionMenuItemClick(actionType: string) {
        // For action menu item click, also record the user's site role
        const sessionManagementService = getSessionManagementServiceInstance();
        // Explicitly check for Server Admin because sessionSiteRole method does not return Server Admin
        const userSiteRole = sessionManagementService.isSessionServerAdmin()
          ? SiteRoles.ServerAdministrator
          : sessionManagementService.sessionSiteRole();

        Telemetry.sendEvent<ActionMenuItemClickedEvent>({
          ...commonData,
          ...specificData,
          type: TelemetryEvents.ActionMenuItemClicked,
          actionType,
          userSiteRole
        });
      };
    }
  }
}

function mapExplanationTypeToRecType(explanationType: string): ChannelTypes {
  switch(explanationType) {
    case PopularExplanation:
      return ChannelTypes.recsPopular;
    case SimiliarUsersExplanation:
      return ChannelTypes.recsSimilarUsers;
    default:
      return ChannelTypes.recommendations;
  }
}
